import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { API } from '../../services';
import { apiConfig } from '../../configs';

// Shows the live Baileys WhatsApp connection state in the header, and lets the user
// link/re-link the business WhatsApp number without ever needing server/SSH access --
// clicking "Connect WhatsApp" opens an in-app popup showing the QR code, fetched
// straight from the backend over the normal authenticated API (see
// server/controller/whatsAppStatus.controller.ts's `qr` endpoint).
const BADGE_POLL_INTERVAL_MS = 15000;
const QR_POLL_INTERVAL_MS = 4000; // faster while the popup is open and someone's waiting to scan

const STATUS_META = {
    connected: { label: 'WhatsApp Connected', className: 'bg-success-subtle text-success' },
    waiting_for_scan: { label: 'WhatsApp: Scan QR', className: 'bg-warning-subtle text-warning' },
    connecting: { label: 'WhatsApp: Connecting…', className: 'bg-secondary-subtle text-secondary' },
    disconnected: { label: 'WhatsApp: Not Connected', className: 'bg-danger-subtle text-danger' },
    disabled: { label: 'WhatsApp: Disabled', className: 'bg-secondary-subtle text-secondary' },
};

const QR_STATUS_MESSAGE = {
    connecting: 'Connecting to WhatsApp…',
    waiting_for_scan: 'Open WhatsApp on the phone you want to send notifications from, go to Settings > Linked Devices > Link a Device, and scan this code.',
    disconnected: 'Disconnected. Ask your developer to restart the server to get a fresh QR code.',
    disabled: 'WhatsApp notifications are turned off on the server.',
};

const WhatsAppStatusBadge = () => {
    const [status, setStatus] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [qr, setQr] = useState(null);
    const [modalStatus, setModalStatus] = useState(null);
    const qrIntervalRef = useRef(null);

    const fetchStatus = useCallback(() => {
        API.get(apiConfig.whatsappStatus)
            .then((res) => setStatus(res?.status || null))
            .catch(() => {
                // Background poll -- fail silently rather than toasting an error on
                // every header render if the endpoint is briefly unreachable.
            });
    }, []);

    useEffect(() => {
        fetchStatus();
        const interval = setInterval(fetchStatus, BADGE_POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [fetchStatus]);

    const fetchQr = useCallback(() => {
        API.get(apiConfig.whatsappQr)
            .then((res) => {
                setModalStatus(res?.status || null);
                setQr(res?.qr || null);
                if (res?.status === 'connected') {
                    setStatus('connected'); // update the header badge immediately too
                }
            })
            .catch(() => {
                // Leave whatever was last shown in the popup rather than clearing it.
            });
    }, []);

    const openModal = () => {
        setModalOpen(true);
        setQr(null);
        setModalStatus(status);
        fetchQr();
        qrIntervalRef.current = setInterval(fetchQr, QR_POLL_INTERVAL_MS);
    };

    const closeModal = () => {
        setModalOpen(false);
        if (qrIntervalRef.current) {
            clearInterval(qrIntervalRef.current);
            qrIntervalRef.current = null;
        }
    };

    // Auto-close the popup a couple seconds after a successful scan.
    useEffect(() => {
        if (modalOpen && modalStatus === 'connected') {
            const timeout = setTimeout(closeModal, 2000);
            return () => clearTimeout(timeout);
        }
    }, [modalOpen, modalStatus]);

    useEffect(() => {
        // Stop polling if the component unmounts while the popup is open.
        return () => {
            if (qrIntervalRef.current) clearInterval(qrIntervalRef.current);
        };
    }, []);

    if (!status) {
        return null;
    }

    const meta = STATUS_META[status] || STATUS_META.disconnected;
    const showConnectButton = status !== 'connected' && status !== 'disabled';

    return (
        <div className="ms-2 header-item d-none d-sm-flex align-items-center">
            <span className={`badge rounded-pill px-2 py-1 fs-12 ${meta.className}`}>
                <i className="bx bxl-whatsapp align-middle me-1"></i>
                {meta.label}
            </span>
            {showConnectButton && (
                <button type="button" onClick={openModal} className="btn btn-sm btn-soft-success ms-2">
                    Connect WhatsApp
                </button>
            )}

            <Modal isOpen={modalOpen} toggle={closeModal} centered>
                <ModalHeader toggle={closeModal}>WhatsApp Notification Setup</ModalHeader>
                <ModalBody className="text-center pb-4">
                    <p className="text-muted mb-3">{QR_STATUS_MESSAGE[modalStatus] || 'Loading…'}</p>
                    {modalStatus === 'waiting_for_scan' && (
                        qr ? (
                            <img src={qr} alt="WhatsApp QR code" width="280" height="280" className="border rounded p-2 bg-white" />
                        ) : (
                            <div className="py-5">Generating QR code…</div>
                        )
                    )}
                    {modalStatus === 'connected' && (
                        <div className="text-success" style={{ fontSize: 56 }}>
                            <i className="bx bx-check-circle"></i>
                        </div>
                    )}
                    {modalStatus === 'connecting' && (
                        <div className="py-5 text-muted">
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Please wait…
                        </div>
                    )}
                </ModalBody>
            </Modal>
        </div>
    );
};

export default WhatsAppStatusBadge;
